<template>
  <v-row class="match-height">
    <v-col
      cols="12"
      md="12"
    >
      <v-card>
        <v-card-title>
          General Report
        </v-card-title>
        <v-card-text>
          <v-form
            ref="reportForm"
            class="multi-col-validation mt-6"
            lazy-validation
            @submit.prevent="performSearch"
          >
            <v-row>
              <v-col
                md="4"
                cols="12"
              >
                <v-text-field
                  v-model="dateFrom"
                  label="Date From"
                  dense
                  outlined
                  :prepend-inner-icon="icons.mdiCalendar"
                  placeholder="MM-DD-YYYY"
                  :rules="reportRules.dateRules"
                ></v-text-field>
              </v-col>
              <v-col
                md="4"
                cols="12"
              >
                <v-text-field
                  v-model="dateTo"
                  label="Date To"
                  dense
                  outlined
                  :prepend-inner-icon="icons.mdiCalendar"
                  placeholder="MM-DD-YYYY"
                  :rules="reportRules.dateRules"
                ></v-text-field>
              </v-col>
              <v-col
                md="4"
                cols="12"
                type="submit"
              >
                <v-btn
                  color="primary"
                  :loading="loadingBtn"
                  type="submit"
                >
                  Submit
                </v-btn>
                <v-btn
                  type="reset"
                  class="mx-2"
                  outlined
                >
                  Reset
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider class="my-4"></v-divider>

        <v-card-text
          class="pt-10"
        >
          <v-data-table
            :headers="headers"
            :items="usersList"
            :loading="loadingData"
            loading-text="Loading... Please wait"
          >
            <!-- name -->
            <template #[`item.name`]="{item}">
              <div class="d-flex flex-column">
                <span class="d-block font-weight-semibold text--primary text-truncate">{{ item.name }}</span>
                <!-- <small>{{ item.id }}</small> -->
              </div>
            </template>
            <!-- household -->
            <template #[`item.household`]="{item}">
              <div class="d-flex flex-column">
                <span class="d-block font-weight-semibold text--primary text-truncate">{{ item.household }}</span>
                <small>members</small>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col
      cols="12"
    >
      <v-card class="mt-5">
        <v-card-title class="align-start">
          <span>Breakdown by ethnicity</span>

          <v-spacer></v-spacer>
        </v-card-title>

        <v-card-text>
          <!-- ethnicity Breakdown table -->
          <div class="mt-5">
            <template class="mt-5">
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        Category
                      </th>
                      <th class="text-left">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in ethnicityBreakdownTableComputed"
                      :key="item.id"
                    >
                      <td>{{ item.ethnicity }}</td>
                      <td>{{ item.total }} <small>members</small></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col
      cols="12"
    >
      <v-card class="mt-5">
        <v-card-title class="align-start">
          <span class="font-weight-semibold">Statistics</span>
          <v-spacer></v-spacer>
        </v-card-title>

        <v-card-subtitle class="mb-8 mt-n5">
          <span class="font-weight-semibold text--primary me-1">Apply the filters to get updated statistics</span>
        </v-card-subtitle>

        <v-card-text>
          <v-row>
            <v-col
              v-for="(data, index) in statisticsDataComputed"
              :key="index"
              cols="12"
              md="4"
              class="d-flex align-center"
            >
              <v-avatar
                size="44"
                :color="resolveStatisticsIconVariation (data.title).color"
                rounded
                class="elevation-1"
              >
                <v-icon
                  dark
                  color="white"
                  size="30"
                >
                  {{ resolveStatisticsIconVariation (data.title).icon }}
                </v-icon>
              </v-avatar>
              <div class="ms-3">
                <p class="text-xs mb-0">
                  {{ data.title }}
                </p>
                <h3 class="text-xl font-weight-semibold">
                  <span
                    v-if="(data.title === 'Export')"
                  >
                    <a
                      @click="generatePDF()"
                    >
                      {{ data.total }}
                    </a>
                  </span>
                  <span
                    v-else
                  >
                    {{ data.total }}
                  </span>
                </h3>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col
      cols="12"
    >
      <vue-html2pdf
        ref="html2Pdf"
        :show-layout="false"
        :float-layout="true"
        :enable-download="true"
        :preview-modal="true"
        :paginate-elements-by-height="1400"
        :filename="pdfFilename"
        :pdf-quality="2"
        :manual-pagination="false"
        pdf-format="a4"
        :pdf-margin="10"
        pdf-orientation="portrait"
        pdf-content-width="800px"
        @progress="onProgress($event)"
      >
        <section slot="pdf-content">
          <general-report-pdf-content
            :chart-data="ethnicityBreakdownTableComputed"
            :statistics-data="statisticsDataComputed"
            :selected-dates="reportDates"
          >
          </general-report-pdf-content>
        </section>
      </vue-html2pdf>
    </v-col>

    <!--
    // testing
    <div @click="generatePDF()" class="np-btn">Generate PDF</div>
    <div>
      <general-report-pdf-content
        :chart-data="ethnicityBreakdownTableComputed"
        :statistics-data="statisticsDataComputed"
        :selected-dates="reportDates"
      >
      </general-report-pdf-content>
    </div>
    -->
  </v-row>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import {
  mdiTrendingUp,
  mdiAccountOutline,
  mdiApplicationExport,
  mdiEmailSend,
  mdiCalendar,
} from '@mdi/js'
import { ref } from '@vue/composition-api'

import Swal from 'sweetalert2'
import VueHtml2pdf from 'vue-html2pdf'
import { axiosInst } from '@/utils/axiosIns'
import generalReportPdfContent from '@/components/history/general-report-pdf-content.vue'
import { reportRules } from '@/utils/form-rules'

export default {
  components: {
    VueHtml2pdf,
    generalReportPdfContent,
  },

  setup() {
    const usersList = ref([])
    const origUsersList = ref([])
    const usersData = ref([])

    const loadingBtn = ref(false)

    return {
      search: '',
      customSearch: '',
      headers: [
        {
          text: 'Name',
          align: 'start',

          // sortable: false,
          value: 'name',
        },
        { text: 'Ethnicity', value: 'ethnicity' },
        { text: 'Household', value: 'household' },
        { text: 'Date, Time', value: 'createdAt' },

        // { text: 'Carbs (g)', value: 'carbs' },
        // { text: 'Responsable', value: 'createdBy' }
      ],
      usersList,
      origUsersList,
      menu: false,

      // icons
      icons: {
        mdiTrendingUp,
        mdiAccountOutline,
        mdiApplicationExport,
        mdiEmailSend,
        mdiCalendar,
      },

      // isAdmin: false,
      // userData: getUserData() || { username: 'Unknown', role: 'Guest' },
      usersData,
      loadingData: false,

      // TODO: this should connect to all. Create a centralized thing for this
      ethnicitiesNames: [
        {
          id: 0,
          shortName: 'indian',
          name: 'American Indian or Alaska Native',
        },
        {
          id: 1,
          shortName: 'asian',
          name: 'Asian',
        },
        {
          id: 2,
          shortName: 'black',
          name: 'Black or African American',
        },
        {
          id: 3,
          shortName: 'hispanic',
          name: 'Hispanic or Latino',
        },
        {
          id: 4,
          shortName: 'islander',
          name: 'Native Hawaiian or Other Pacific Islander',
        },
        {
          id: 5,
          shortName: 'white',
          name: 'White',
        },
        {
          id: 6,
          shortName: 'other',
          name: 'Other',
        },
      ],

      pdfFilename: `general-report-${new Date().toJSON().slice(0, 10)}`,
      loadingBtn,
      dateFrom: undefined,
      dateTo: undefined,
      reportRules: reportRules(),
    }
  },

  computed: {
    ethnicityBreakdownTableComputed() {
      const totalsByEthnicity = this.usersList.reduce((acc, user) => {
        const ethnicity = user.ethnicity || 'Other'
        const household = Number(user.household) || 0

        acc[ethnicity] = (acc[ethnicity] || 0) + household

        return acc
      }, {})

      return this.ethnicitiesNames.map(item => ({
        id: item.id,
        ethnicity: item.name || 'Unknown',
        total: totalsByEthnicity[item.name] || 0,
      }))
    },

    statisticsDataComputed() {
      // totals
      return [
        {
          title: 'Clients',
          total: this.usersList.map(user => user.id).length || 0,
        },
        {
          title: 'Total Served',
          total: this.usersList.map(user => user.household).reduce((a, b) => a + b, 0) || 0,
        },

        /*
        {
          title: 'Email',
          total: '0',
        },
        */

        {
          title: 'Export',
          total: 'PDF',
        },
      ]
    },

    reportDates() {
      return (this.dateFrom && this.dateTo) ? `${this.dateFrom} to ${this.dateTo}` : ''
    },
  },

  methods: {
    notifyError(message) {
      Swal.fire({
        position: 'top-end',
        icon: 'error',
        title: message,
        showConfirmButton: false,
        timer: 1500,
      })

      console.error(message)
    },

    async performSearch() {
      if (!this.$refs.reportForm.validate()) return

      await this.getData({
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
      })
    },

    async getData({ dateFrom, dateTo } = {}) {
      try {
        // if (!dateFrom) return this.notifyError('Date From is required')
        // if (!dateTo) return this.notifyError('Date To is required')

        this.resetData()
        this.loadingData = true
        this.loadingBtn = true

        const rs = await axiosInst.post('client-posts', { dateFrom, dateTo })

        if (rs.status === 200 && rs.data.error === false) {
          if (rs.data && rs.data.r2) this.usersData = rs.data.r2 || []

          const mappedUsers = (rs.data.r || []).map(post => {
            // eslint-disable-next-line no-underscore-dangle
            const userFound = this.usersData.find(user => user._id === post.id) || {}

            return {
              postId: post.id, // post id if I need to make a transaction
              name: userFound.name || 'Unknown',
              ethnicity: userFound.ethnicity || 'Other',
              household: (userFound.household && userFound.household.length ? userFound.household.length : 0) + 1,
              createdAt: new Date(post.createdAt).toLocaleString(),

              // createdBy: post.createdBy
              createdAtRaw: post.createdAt,
            }
          })

          this.usersList = mappedUsers
          this.origUsersList = [...mappedUsers]
        }
      } catch (err) {
        console.error(err)

        const msg = err && err.response && err.response.data && err.response.data.r
          ? err.response.data.r
          : 'Unable to load report data'
        this.notifyError(msg)
      } finally {
        this.loadingData = false
        this.loadingBtn = false
      }
    },

    resolveStatisticsIconVariation: data => {
      if (data === 'Clients') return { icon: mdiAccountOutline, color: 'primary' }
      if (data === 'Total Served') return { icon: mdiTrendingUp, color: 'success' }
      if (data === 'Export') return { icon: mdiApplicationExport, color: 'warning' }
      if (data === 'Email') return { icon: mdiEmailSend, color: 'info' }

      return { icon: mdiAccountOutline, color: 'success' }
    },

    onProgress(event) {
      console.log(`Processed: ${event} / 100`)
    },

    generatePDF() {
      this.$refs.html2Pdf.generatePdf()
    },

    resetData() {
      this.usersList = []
      this.origUsersList = []
      this.usersData = []
    },
  },
}
</script>
